/**
 * The live invoice preview beside the Create Invoice form (OR-1449).
 *
 * Sticky while the form scrolls, so the underwriter can watch the document
 * take shape as they fill it in — the whole point being that they never
 * have to cross-check a number against Ascend by eye.
 *
 * Every empty value renders as an em dash rather than collapsing, so the
 * shape of the finished invoice is visible from the first render (the
 * Figma's "form partially filled" frame).
 */
import type { ReactNode } from "react";
import { cx } from "@tricura/atlas-ui";
import { COVERAGE_TYPE_LABELS, type AscendCoverageType } from "../../../api";

const DASH = "—";

export interface InvoiceSummaryData {
    invoiceNumber: string;
    dueDate: string;
    billToName: string;
    billToEmail: string;
    billToAddress: string;
    insuredName: string;
    insuredAddress: string;
    policyNumber: string;
    coverageType: AscendCoverageType | null;
    effectiveDate: string;
    expirationDate: string;
    selectedPremium: number | null;
    taxes: number | null;
    surcharges: number | null;
    policyFee: number | null;
    brokerageCommission: number | null;
    brokerageCommissionPct: number | null;
    /** The backend-computed invoice total (`ascend_total_amount_cents`),
     *  present only once the sync completes. Never derived here. */
    dueValue: number | null;
}

function money(value: number | null): string {
    if (value == null) return DASH;
    return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function text(value: string): string {
    return value.trim() === "" ? DASH : value;
}

/** `MM/DD/YYYY` → `September 2nd, 2026`, the header's long form. */
function longDate(value: string): string {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!m) return DASH;
    const [, mm, dd, yyyy] = m;
    const monthName = new Date(Number(yyyy), Number(mm) - 1, 1).toLocaleDateString("en-US", { month: "long" });
    const day = Number(dd);
    const suffix =
        day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
    return `${monthName} ${day}${suffix}, ${yyyy}`;
}

/**
 * Label/value pair inside the grey detail card.
 *
 * The LABEL is the dark one and the value sits back in grey — the reverse
 * of the usual form treatment, and deliberate in the Figma: this panel is
 * scanned down the left edge to find a field, not read across. The Total
 * is the exception and keeps its own brand colour and size.
 */
function DetailRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4">
            <span className="text-primary text-sm">{label}</span>
            <span className="text-quaternary text-sm">{value}</span>
        </div>
    );
}

/** A titled block inside the grey detail card, e.g. "Policy". */
function DetailGroup({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="flex gap-4">
            {/* Recedes into the same grey as the values, leaving the row
              * labels as the only dark text in the card. */}
            <span className="text-quaternary w-28 shrink-0 text-sm">{title}</span>
            <div className="flex flex-1 flex-col gap-1.5">{children}</div>
        </div>
    );
}

/**
 * Rule between the card's blocks. Rendered as a sibling of the blocks
 * rather than inside one, so it spans the card's full inner width —
 * including the label column — the way the Figma draws it. The last one
 * before the Total is dashed, marking a sum rather than a new section.
 */
function Rule({ dashed }: { dashed?: boolean }) {
    return <div aria-hidden="true" className={cx("border-secondary border-t", dashed && "border-dashed")} />;
}

export function InvoiceSummary({ data }: { data: InvoiceSummaryData }) {
    // NO arithmetic here: the total is the backend's own figure, captured
    // when the sync completes. Before that it is honestly a dash.
    const total = data.dueValue;

    return (
        <aside
            // `self-start` is what makes the sticky work: as a grid child the
            // aside stretches to the full track height by default, leaving it
            // nothing to scroll against.
            className="border-secondary bg-primary shadow-xs flex flex-col gap-5 rounded-xl border p-5 lg:sticky lg:top-0 lg:self-start"
            data-autotest="invoice-summary"
        >
            <header className="flex flex-col gap-1">
                <h3 className="text-primary text-lg font-semibold">{text(data.insuredName)}</h3>
                <p className="text-quaternary text-sm">
                    #{text(data.invoiceNumber)} · Due on {longDate(data.dueDate)}
                </p>
            </header>

            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <span className="text-quaternary text-sm">Bill to</span>
                    <span className="text-primary text-sm font-semibold">{text(data.billToName)}</span>
                    <span className="text-quaternary text-sm">{text(data.billToEmail)}</span>
                    <span className="text-tertiary text-sm">{text(data.billToAddress)}</span>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                    {/* Figma: Text xs / Regular, text-quaternary (#737373). */}
                    <span className="text-quaternary mt-0.5 text-xs">Due Value</span>
                    <span className="text-brand-secondary text-lg font-semibold tabular-nums">{money(total)}</span>
                </div>
            </div>

            <div className="bg-secondary flex flex-col gap-5 rounded-lg p-4">
                <DetailGroup title="Insured">
                    <span className="text-primary text-sm font-semibold">{text(data.insuredName)}</span>
                    <span className="text-primary text-sm">{text(data.insuredAddress)}</span>
                </DetailGroup>

                <Rule />

                <DetailGroup title="Policy">
                    <DetailRow label="Policy number" value={text(data.policyNumber)} />
                    <DetailRow
                        label="Coverage"
                        value={data.coverageType ? COVERAGE_TYPE_LABELS[data.coverageType] : DASH}
                    />
                    <DetailRow label="Effective" value={text(data.effectiveDate)} />
                    <DetailRow label="Expiration" value={text(data.expirationDate)} />
                </DetailGroup>

                <Rule />

                <DetailGroup title="Premium & Fees">
                    <DetailRow label="Selected Premium" value={money(data.selectedPremium)} />
                    <DetailRow label="Taxes" value={money(data.taxes)} />
                    <DetailRow label="Surcharges" value={money(data.surcharges)} />
                    <DetailRow label="Policy Fee" value={money(data.policyFee)} />
                    <DetailRow
                        label="Brokerage commission"
                        value={
                            data.brokerageCommissionPct == null ? (
                                money(data.brokerageCommission)
                            ) : (
                                <>
                                    {/* Same grey as the amount beside it, just
                                      * heavier — it qualifies the figure
                                      * rather than competing with it. Colour
                                      * is inherited on purpose. */}
                                    <span className="font-semibold">({data.brokerageCommissionPct}%)</span>{" "}
                                    {money(data.brokerageCommission)}
                                </>
                            )
                        }
                    />
                </DetailGroup>

                <Rule dashed />

                {/* Right-aligned as a pair, not label-left/value-right like
                  * the rows above it — the Figma sets the total apart. */}
                <div className="flex items-baseline justify-end gap-3">
                    <span className="text-quaternary text-sm">Total</span>
                    <span className="text-brand-secondary text-lg font-semibold tabular-nums">{money(total)}</span>
                </div>
            </div>
        </aside>
    );
}
